import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, map, of } from "rxjs";
import { AccessService } from "src/app/core/services/access.service";
import { environment as env } from "src/environments/environment";
import { localDB } from "src/app/shared/helpers/localStorage";
import { groupBy } from "src/app/shared/helpers/groupBy";
import * as moment from "moment";
import { SmartAlertService } from "src/app/shared/components/smart-alert/smart-alert.service";
import { GenericResponse } from "src/app/shared/interfaces/generic.interface";
import { RevisionService } from "src/app/core/services/revision.service";
import { AAllTareoStatus, AllCatfilter, AllPry_RqFilter, FooterTotals, Observation, ObservationResponse, PayForm, PayTaskSelected, tareoStatusV2, TaskData, TaskHistoric, TempTaskModal } from "../interfaces/task.interface";
import { GenericResponseList } from "src/app/shared/interfaces/table.interface";
import { getInputsPart2, getMinutesInSchedule, mapInputsToSend, marksOfJornada, paginateState } from "../helpers/functions";
import { mapDataToFilterIndicator, mapTareoResponse, parseInputs, transformFooterData, transformTaskList } from "../helpers/mapData";
import { MarkDetail } from "src/app/shared/components/maps/interface/interface";

@Injectable({
  providedIn: "root",
})
export class TaskService {
  data = {}
  private readonly pryRqSubject: any | null = null
  pryRqFilterData: Observable<any | null> = of(null)
  revision: any = null
  errorTask = new BehaviorSubject<TaskData[]>([]);
  private readonly summaryReportTrigger = new Subject<void>();
  readonly summaryReportTrigger$ = this.summaryReportTrigger.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly _accesService: AccessService,
    private readonly _smartAlert: SmartAlertService,
    private readonly _revisionService: RevisionService

  ) {
    if (_accesService.validarPermisoSlug('TASK-LIST-TEAM')) {
      this.pryRqSubject = new BehaviorSubject<GenericResponse<AllPry_RqFilter> | null>(null)
      this.pryRqFilterData = this.pryRqSubject.asObservable()
      this.getFilterPry_Eq()
      this._revisionService.revision$.subscribe(x => {
        if (x?.REVISION[0]) {
          this.revision = x.REVISION[0].sCodigo || null;
        };
      })
    }
  }

  requestSummaryReport(): void {
    this.summaryReportTrigger.next();
  }

  getAll(
    records: number,
    sort: string,
    order: string,
    numPage: number,
    getInputs = ""
  ): Observable<GenericResponseList<TaskData>> {
    const baseData = {
      numPage: numPage + 1,
      order,
      sort,
      records,
      numFilter: null,
      textFilter: null,
      sTipo_listado: "",
      sFilterOne: null,
      sFilterTwo: null,
      sFilterThree: null,
      sFilterFour: null,
      sFilterFive: null,
      sFilterSix: null,
      sFilterSeven: null,
      sFilterEight: null,
      sFilterNine: null,
      dFecha_Inicio: null,
      dFecha_Fin: null,
      bDownload: false,
      bListado_individual: false,
      sFilterNotification: null,
    };
    
    const parsedInputs = parseInputs(getInputs);
    const data = { ...baseData, ...parsedInputs };
    this.data = data

    return this.http.post<GenericResponseList<TaskData>>(`${env.api_url}api/tareo/listar`, data).pipe(
      map((response) => {
        let errors = this.errorTask.getValue()
        let resp = transformTaskList(response, this._accesService, data.sTipo_listado, errors);
        paginateState.nRecord = response.paginate.nRecord;
        paginateState.nRegisters = response.paginate.nRegisters;
        this.errorTask.next([])
        return resp
      })
    );
  }

  getFooter(
    getInputs = "",
    records: number,
    sort: string,
    order: string,
    numPage: number
  ): Observable<GenericResponse<FooterTotals>> {
    let data = {
      numPage: numPage + 1,
      order: order,
      sort: sort,
      records: records,
      numFilter: null,
      textFilter: null,
      sTipo_listado: "",
      sFilterOne: '',
      sFilterTwo: null,
      sFilterThree: null,
      sFilterFour: null,
      sFilterFive: null,
      sFilterSix: null,
      sFilterSeven: null,
      sFilterEight: null,
      sFilterNine: null,
      dFecha_Inicio: null,
      dFecha_Fin: null,
      bDownload: false,
      bListado_individual: false,
      sFilterNotification: null
    };

    const newInputs = getInputsPart2(getInputs);
    this.data = mapInputsToSend(newInputs, data);

    return this.http.post<GenericResponse<FooterTotals>>(`${env.api_url}api/tareo/listarTotalGeneral`, this.data).pipe(map((response) => transformFooterData(response)));
  }

  getFilterCategories(aId_Requerimiento: number[]): Observable<GenericResponse<AllCatfilter>> {
    let requestUrl = `${env.api_url}api/filtrosTareo/RequerimientosCategorias`
    return this.http.post<GenericResponse<AllCatfilter>>(requestUrl, aId_Requerimiento).pipe(
      map(resp => {
        let response = resp
        if (resp.isSuccess) {
          response = {
            ...resp,
            data: {
              'aRequerimientos': mapDataToFilterIndicator(resp.data['aRequerimientos'], 'nId_Requerimiento', 'aCategorias', 'Estado_Requerimiento')
            }
          };
        } else {
          this._smartAlert.init({
            type: 'danger',
            message: response.message
          })
        }
        return response
      })
    )
  }

  updateFilterPry_Rq(rqs: number[] | null): Observable<GenericResponse<boolean>> {
    // this.pryRqSubject.next(null)
    return this.http.post<GenericResponse<boolean>>(`${env.api_url}api/filtrosTareo/recalcular`, rqs).pipe(
      map(resp => {
        if (resp.isSuccess && this._accesService.validarPermisoSlug('TASK-LIST-TEAM')) {
          this.getFilterPry_Eq()
        } return resp
      })
    )
  }

  getFilterPry_Eq() {
    this.http.get<GenericResponse<AllPry_RqFilter>>(`${env.api_url}api/filtrosTareo/ProyectosRequerimientos`).pipe(
      map(resp => {
        let response = {
          ...resp,
          data: {
            'aProyectos': mapDataToFilterIndicator(resp.data['aProyectos'], 'nId_Proyecto', 'aRequerimiento', 'Estado_Proyecto', 'Estado_Requerimiento'),
            'aEquipos': mapDataToFilterIndicator(resp.data['aEquipos'], 'nId_Proyecto', 'aRequerimiento', 'Estado_Proyecto', 'Estado_Requerimiento'),
            'aTodos': mapDataToFilterIndicator(resp.data['aTodos'], 'nId_Proyecto', 'aRequerimiento', 'Estado_Proyecto', 'Estado_Requerimiento'),
          }
        };
        return response
      })
    ).subscribe({
      next: (response) => {
        this.pryRqSubject.next(response)
      },
      error: (errorResponse) => {
        console.error('Error filtro', errorResponse)
        this.pryRqSubject.next({
          isSuccess: false,
          data: null
        })
      }
    })
  }

  payTaskSelected(tasks: { aTasks: TaskData[], payForm: PayForm }): Observable<GenericResponse<any>> {
    return this.http.put<GenericResponse<PayTaskSelected>>(`${env.api_url}api/tareo/actualizar-estado/seleccion`, tasks)
  }

  payAllTaskSelected(payForm: PayForm, currentFilters: any): Observable<GenericResponse<any>> {
    const payloadFilters = {
      ...currentFilters,
      sTipo_listado: currentFilters.listType,
      sFilterOne: currentFilters.filterOne,
      sFilterTwo: currentFilters.filterTwo,
      sFilterThree: currentFilters.filterThree,
      sFilterFour: currentFilters.filterFour,
      sFilterFive: currentFilters.filterFive,
      sFilterSix: currentFilters.filterSix,
      sFilterSeven: currentFilters.filterSeven,
      sFilterEight: currentFilters.filterEight,
      sFilterNine: currentFilters.filterNine,
      dFecha_Inicio: currentFilters.startDate,
      dFecha_Fin: currentFilters.endDate,
      sort: currentFilters.sortBy,
      order: currentFilters.sortDir,
      bListado_individual: currentFilters.isIndividualListing
    };
    const data = { filter: payloadFilters, payForm };
    return this.http.put<GenericResponse<any>>(`${env.api_url}api/tareo/actualizar-estado/filtro`, data);
  }

  massiveEditTask(tasks: any[], data: any): Observable<GenericResponse<any>> {
    let dataToSend = {
      tasks: tasks,
      pryRqCatCodes: data
    }
    return this.http.post<any>(`${env.api_url}api/tareo/editarPryRqCat/seleccion`, dataToSend);
  }

  massiveAllEditTask(data: any): Observable<GenericResponse<any>> {
    let dataToSend = {
      filter: this.data,
      pryRqCatCodes: data
    }
    return this.http.post<any>(`${env.api_url}api/tareo/editarPryRqCat/filtro`, dataToSend);
  }

  deleteTask(sId_Tasks: string): Observable<any> {
    return this.http.delete<any>(`${env.api_url}api/tareo/eliminar?sId_Tasks=${sId_Tasks}`);
  }

  editTask(nId_Task: number, data: any): Observable<any> {
    return this.http.put<any>(`${env.api_url}api/tareo/actualizar`, data);
  }

  addObserve(data: Observation[]) {
    return this.http.post<any>(env.api_url + "api/observacion/seleccion/crear", data);
  }

  observeAllTask(obs: Observation) {
    let data = { sObservacion: obs.sObservacion, oFilter: this.data }
    return this.http.post<any>(env.api_url + "api/observacion/filtro/crear", data);
  }

  getObserve(nId_Task: number | string) {
    return this.http.get<GenericResponse<ObservationResponse[]>>(`${env.api_url}api/tareo/listado-observaciones?nId_Task=${nId_Task}`);
  }

  getHistorical(nId_Task: number) {
    return this.http.get<GenericResponse<TaskHistoric[]>>(`${env.api_url}api/tareo/listado-historicos?nId_Task=${nId_Task}`);
  }

  addTask(params: TempTaskModal[]) {
    const fechaRegistro: string = params.at(-1)!.dFecha_Registro as string;
    const horarioData = localDB.getItem('V-HORARIO');
    let sLastWorkDay: string = fechaRegistro;
    try {
      let vHorario = horarioData ? JSON.parse(horarioData) : null;
      if (Array.isArray(vHorario)) {
        vHorario = { data: vHorario };
      }
      if (vHorario?.data?.length) {
        const ultimoHorario = vHorario.data
          .filter((item: { dtime: any; }) => item?.dtime)
          .sort((a: { dtime: string | number | Date; }, b: { dtime: string | number | Date; }) =>
            new Date(b.dtime).getTime() - new Date(a.dtime).getTime())[0];

        sLastWorkDay = ultimoHorario?.dtime || fechaRegistro;
      }
    } catch (error) {
      console.error('Error parsing V-HORARIO:', error);
    }
    const requestUrl: string = `${env.api_url}api/tareo/crear`;
    const newParams: TempTaskModal[] = params.map(param => ({
      ...param,
      sLastWorkDay,
    }));

    return this.http.post<any>(requestUrl, newParams);
  }

  approveTask(params: any) {
    return this.http.post<any>(`${env.api_url}api/tareo/aprobar`, params);
  }

  approveAllTask(): Observable<any> {
    return this.http.post<any>(`${env.api_url}api/tareo/aprobarTodas`, this.data);
  }

  endRevisionTask() {
    return this.http.get<GenericResponse<boolean>>(`${env.api_url}api/tareo/quitar_revision`);
  }

  initRevisionTask(
    records: number,
    sort: string,
    order: string,
    numPage: number,
    getInputs = "") {
    let data = {
      numPage: numPage + 1,
      order: order,
      sort: sort,
      records: records,
      numFilter: null,
      textFilter: null,
      sTipo_listado: "",
      sFilterOne: '',
      sFilterTwo: null,
      sFilterThree: null,
      sFilterFour: null,
      sFilterFive: null,
      sFilterSix: null,
      sFilterSeven: null,
      sFilterEight: null,
      dFecha_Inicio: null,
      dFecha_Fin: null,
      bDownload: false,
      bListado_individual: false,
      sFilterNotification: null
    }
    let newInputs = getInputsPart2(getInputs)
    data = mapInputsToSend(newInputs, data)

    return this.http.post<GenericResponse<boolean>>(`${env.api_url}api/tareo/revisar`, data)
  }


  getOccupiedNonRegularRanges(
    nId_Colaborador: number,
    dFecha: string,
    nId_Tarea?: number | null
  ): Observable<GenericResponse<{ dInicio: string; dFin: string }[]>> {
    const exclude = nId_Tarea ? `&nId_Tarea=${nId_Tarea}` : '';
    return this.http.get<GenericResponse<{ dInicio: string; dFin: string }[]>>(
      `${env.api_url}api/tareo/rangos-no-regulares-ocupados?nId_Colaborador=${nId_Colaborador}&dFecha=${dFecha}${exclude}`
    );
  }

  getTareoStatusV2(idCollab?: string | number): Observable<tareoStatusV2> {
    let idColab =
      !!idCollab ? idCollab : Number.parseInt(localDB.getItem("idColaborador"));

    return this.http
      .get<GenericResponse<AAllTareoStatus[]>>(
        env.api_url + "api/configuraciones/tareo_status?nId_User=" + idColab
      )
      .pipe(
        map((response) => {
          let today = moment().startOf('day');
          if (moment().hour() <= 6) {
            today = moment().subtract(1, 'day').startOf('day');
          }
          const intervalsObj = JSON.parse(localDB.getItem('Intervals') || 'null')?.aIntervals || [];
          const scheduleIntervals = JSON.parse(localDB.getItem('V-HORARIO') || '[]');
          const aIntervals: MarkDetail[] = marksOfJornada(intervalsObj, today.format('YYYY-MM-DD'));
          let { dentro, fuera, endHour } = getMinutesInSchedule(aIntervals, scheduleIntervals);

          let newData: AAllTareoStatus[] = response.data
            .map((x) => {
              x.dDate = moment(x.dDate.split("T")[0]).format('YYYY-MM-DD');
              if (moment(x.dDate).isSame(today, 'day')) {
                x.nRequired = dentro
                x.nRequiredNoRegular = fuera
                x.salida = endHour
              }
              return x;
            })
            .filter(x => moment(x.dDate).isSameOrBefore(today, 'day'));

          let oTareoByStatus_base = groupBy(newData, (i: AAllTareoStatus) => i.sEstado);
          let oTareoByStatus: { [key: string]: string[] } = {}
          let datesWithoutMarks = newData
            .filter((x: any) =>
              (['HORARIO_REGULAR', 'SOLICITUD_POR_HORAS'].includes(x.sEstado_Horario)
                && ((moment(x.dDate).isBefore(moment(today)) && x.nReadyMark > 0 && x.nReadyMark < x.nRecomended_Marks)
                  || (moment(x.dDate).isSame(moment(today)) && x.nReadyMark == 0)
                )) ||
              (x.nReadyMark > 0 && (x.nReadyMark % 2) !== 0 && moment(x.dDate).isBefore(moment(today))))
            .map((x: any) => x.dDate);

          for (const key in oTareoByStatus_base) {
            oTareoByStatus[key] = oTareoByStatus_base[key].map(
              (value: any) => value.dDate
            );
          }

          oTareoByStatus['WITHOUT_MARKS'] =
            datesWithoutMarks ?? [];

          oTareoByStatus['FALTA'] =
            oTareoByStatus['FALTA'] ?? [];

          oTareoByStatus['COMPLETO'] =
            oTareoByStatus['COMPLETO'] ?? [];

          oTareoByStatus['INCOMPLETO'] =
            oTareoByStatus['INCOMPLETO'] ?? [];

          oTareoByStatus['REVISION'] =
            oTareoByStatus['REVISION'] ?? [];

          if (Number.parseInt(localDB.getItem("idColaborador")) == idColab) {
            let status = JSON.stringify(response.data)
            localDB.setItem('allTareoStatus', status)
          }

          return { oTareoByStatus, aAllTareoStatus: newData };
        })
      );
  }

  getAllGrouped(
    records: number,
    sort: string,
    order: string,
    numPage: number,
    getInputs = ""
  ): Observable<any> {
    let typoListado = getInputs
      .slice(getInputs.search("sTipo_listado=") + 14)
      .split("&");

    let idUsuario = Number.parseInt(localDB.getItem("idUsuario"));

    let TaskObserve = this._accesService.validarPermisoSlug("TASK-OBSERVE");
    let TaskApprove = this._accesService.validarPermisoSlug("TASK-APPROVE");

    const requestUrl = `${env.api_url}api/tareo/listarAgrupado`;

    let data = {
      numPage: numPage + 1,
      order: order,
      sort: sort,
      records: records,
      numFilter: null,
      textFilter: null,
      sTipo_listado: "",
      sFilterOne: '',
      sFilterTwo: null,
      sFilterThree: null,
      sFilterFour: null,
      sFilterFive: null,
      sFilterSix: null,
      sFilterSeven: null,
      sFilterEight: null,
      dFecha_Inicio: null,
      dFecha_Fin: null,
      bDownload: false,
      bListado_individual: true,
      sFilterNotification: null
    }

    let newInputs = getInputsPart2(getInputs);
    data = mapInputsToSend(newInputs, data);
    this.data = data;

    return this.http.post<any>(requestUrl, data).pipe(
      map((response: any) => {
        if (!response.data) response.data = [];

        response.data = mapTareoResponse(
          response.data,
          idUsuario,
          TaskApprove,
          TaskObserve,
          typoListado
        );

        return response;
      })
    );
  }
}